import { Box, Skeleton } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PremiseConfig, SchemeKey } from "@shared/types/foyer";
import { useContainerSize } from "../../hooks/useContainerSize";
import { tvBase } from "../../utils/preview";

const TV_W = 1920;
const TV_H = 1080;
const RATIO = TV_H / TV_W; // 0.5625

interface Props {
  schemeKey: SchemeKey;
  /** Seed the preview's branding so thumbnails feel like *this* premise. */
  premise?: PremiseConfig;
}

/**
 * A glanceable, live mini-preview of a bespoke TV template, rendered in the
 * template picker so operators can *see* each option before switching.
 *
 * It reuses the standalone `?scheme=<key>` route the TV app already exposes
 * (full-screen scheme against a synthetic config, no backend / pairing), loaded
 * in an iframe and scaled down to fit the card. Because it renders the real
 * scheme, the thumbnail can never drift out of date the way a baked screenshot
 * would. The iframe is inert (`pointer-events: none`, not focusable) so the
 * whole card stays one clickable target.
 */
export default function SchemeThumbnail({ schemeKey, premise }: Props) {
  const { t } = useTranslation("templates");
  const [wrapRef, size] = useContainerSize<HTMLDivElement>();
  const [loaded, setLoaded] = useState(false);
  const width = size.width || TV_W;
  const scale = width / TV_W;
  const height = width * RATIO;

  // Pass the premise branding the standalone scheme route understands, so the
  // mini-preview shows the operator's own hotel name / city, not generic stubs.
  const params = new URLSearchParams({ scheme: schemeKey, preview: "1" });
  const hotel = premise?.premiseName;
  const city = premise?.address?.city;
  if (hotel) params.set("hotel", hotel);
  if (city) params.set("city", city);
  const src = `${tvBase()}/?${params.toString()}`;

  return (
    <Box
      ref={wrapRef}
      sx={{
        position: "relative",
        width: "100%",
        height,
        overflow: "hidden",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#000",
      }}
    >
      {!loaded && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        />
      )}
      <iframe
        title={t("schemeThumbnail.frameTitle", { scheme: schemeKey })}
        src={src}
        loading="lazy"
        tabIndex={-1}
        aria-hidden
        onLoad={() => setLoaded(true)}
        scrolling="no"
        style={{
          width: TV_W,
          height: TV_H,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          display: "block",
          pointerEvents: "none",
          opacity: loaded ? 1 : 0,
          transition: "opacity 200ms ease",
        }}
      />
    </Box>
  );
}
